import { useEffect } from 'react';
import type { ReactNode } from 'react';
import type { ClienteSessao } from '../data/session.js';
import type { Role } from '../data/port.js';
import { HttpConsoleData } from '../data/http.js';
import { useAuth, type EstadoAuthUi } from '../ui/useAuth.js';
import { App } from './App.js';
import type { AppState } from './AppContext.js';

/**
 * Decide o que o operador vê: carregando, login, erro ou o painel.
 *
 * O painel só abre AUTENTICADO, sempre lendo o banco real via HTTP — não existe
 * mais fonte de amostra. Anônimo vai DIRETO para o login do Keycloak, sem tela
 * intermediária. Fica FORA do App de propósito: o painel recebe os papéis já
 * resolvidos, senão existiria um instante em que a tela aceita ação com a
 * permissão errada.
 */

const EXPLICACAO: Record<string, string> = {
  login_expirado: 'O login demorou demais e expirou. Entre de novo — é só isso.',
};

/**
 * Guarda de uma tentativa contra laço de redirecionamento.
 *
 * Se o login voltar ainda anônimo (cookie bloqueado, terceiro-party bloqueado),
 * um auto-redirect cego mandaria para o Keycloak de novo, para sempre. A flag
 * marca "já tentei"; na volta anônima mostramos o cartão manual em vez de
 * repetir. É limpa ao autenticar (ou num clique deliberado em "Entrar").
 */
const GUARD = 'mp_auth_redirect';

const temGuarda = () => {
  try {
    return sessionStorage.getItem(GUARD) === '1';
  } catch {
    return false;
  }
};
const marcarGuarda = () => {
  try {
    sessionStorage.setItem(GUARD, '1');
  } catch {
    /* sessionStorage indisponível — segue sem a proteção */
  }
};
const limparGuarda = () => {
  try {
    sessionStorage.removeItem(GUARD);
  } catch {
    /* idem */
  }
};

interface GateProps {
  cliente: ClienteSessao;
  /** Base da API real — de onde TODOS os dados vêm. */
  baseDaApi: string;
  irPara?: (url: string) => void;
  caminhoAtual?: string;
  erro?: string | null;
}

export function Gate({ cliente, baseDaApi, irPara, caminhoAtual, erro }: GateProps) {
  const estado = useAuth(cliente);
  const navegar = irPara ?? ((url: string) => window.location.assign(url));
  const voltarPara = caminhoAtual ?? window.location.pathname;
  const codigoDeErro = erro !== undefined ? erro : new URLSearchParams(window.location.search).get('erro');

  // Anônimo com erro (login falhou/expirou) ou que já voltou de uma tentativa:
  // mostra o cartão manual em vez de auto-redirecionar (evita laço).
  const mostrarManual = estado.tipo === 'anonimo' && (!!codigoDeErro || temGuarda());

  const irParaLogin = () => {
    marcarGuarda();
    navegar(cliente.urlDeLogin(voltarPara));
  };
  const irParaLoginManual = () => {
    limparGuarda();
    navegar(cliente.urlDeLogin(voltarPara));
  };

  // Origem errada (localhost vs 127.0.0.1) → vai para a certa, em vez de explicar.
  useEffect(() => {
    if (estado.tipo !== 'indisponivel' || !estado.urlCorrigida) return;
    if (new URLSearchParams(window.location.search).has('origem')) return;
    const destino = new URL(estado.urlCorrigida);
    destino.searchParams.set('origem', 'corrigida');
    navegar(destino.href);
  }, [estado, navegar]);

  // Anônimo → direto para o login. A guarda evita repetir na volta anônima.
  useEffect(() => {
    if (estado.tipo === 'autenticado') {
      limparGuarda();
      return;
    }
    if (estado.tipo !== 'anonimo' || mostrarManual) return;
    irParaLogin();
  }, [estado.tipo, mostrarManual]);

  // Enter dispara o login quando o cartão manual está à mostra.
  useEffect(() => {
    if (!mostrarManual) return;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key !== 'Enter') return;
      evento.preventDefault();
      irParaLoginManual();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [mostrarManual]);

  return render(estado, {
    baseDaApi,
    codigoDeErro,
    mostrarManual,
    entrarManual: irParaLoginManual,
    sair: () => cliente.sair(),
  });
}

interface RenderCtx {
  baseDaApi: string;
  codigoDeErro?: string | null;
  mostrarManual: boolean;
  entrarManual: () => void;
  sair: () => Promise<string | null>;
}

function painel(baseDaApi: string, roles: Role[], sessao: AppState['sessao'], sair: () => Promise<string | null>) {
  const data = new HttpConsoleData({ baseUrl: baseDaApi });
  return <App state={{ data, roles, sessao, sair }} />;
}

function render(estado: EstadoAuthUi, ctx: RenderCtx): ReactNode {
  switch (estado.tipo) {
    case 'carregando':
      return (
        <GateShell>
          <div className="row" style={{ gap: '0.6rem', color: 'var(--text-muted)' }}>
            <span className="spinner" /> verificando sessão…
          </div>
        </GateShell>
      );

    case 'anonimo':
      // Caminho normal: já estamos navegando para o Keycloak — mostra um estado
      // breve, sem o cartão de "Entrar".
      if (!ctx.mostrarManual) {
        return (
          <GateShell>
            <div className="row" style={{ gap: '0.6rem', color: 'var(--text-muted)' }}>
              <span className="spinner" /> Redirecionando para o login…
            </div>
          </GateShell>
        );
      }
      // Fallback manual: login falhou/expirou, ou voltou anônimo (cookie bloqueado).
      return (
        <GateShell>
          {ctx.codigoDeErro && (
            <p className="gate__msg gate__msg--warn">
              {EXPLICACAO[ctx.codigoDeErro] ?? 'O login não foi concluído. Tente de novo.'}
            </p>
          )}
          <p className="gate__msg">Sessão não iniciada. Entre para operar a cobrança.</p>
          <button type="button" className="btn btn--primary btn--block" autoFocus onClick={ctx.entrarManual}>
            Entrar
          </button>
        </GateShell>
      );

    case 'autenticado':
      return painel(ctx.baseDaApi, estado.sessao.roles, estado.sessao, ctx.sair);

    case 'desligado':
      return (
        <GateShell>
          <p className="gate__msg gate__msg--error">O login (Keycloak) está desligado neste ambiente.</p>
          <p className="gate__msg">
            O painel precisa de uma sessão para ler o banco. Ative o Keycloak (KEYCLOAK_ENABLED) e
            recarregue.
          </p>
        </GateShell>
      );

    case 'indisponivel':
      return (
        <GateShell>
          <p className="gate__msg gate__msg--error">Não deu para verificar a sessão: {estado.motivo}.</p>
          <p className="gate__msg">
            O painel não abre sem saber quem é você. Confira se o middleware está no ar e recarregue.
          </p>
          <button type="button" className="btn btn--ghost" onClick={() => window.location.reload()}>
            Recarregar
          </button>
        </GateShell>
      );
  }
}

function GateShell({ children }: { children: ReactNode }) {
  return (
    <div className="gate">
      <div className="gate__card">
        <div className="gate__brand">
          <span className="brand-mark">TN</span>
          <span className="gate__title">TELENORTE</span>
        </div>
        <p className="faint" style={{ letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: 'var(--text-xs)' }}>
          Console de cobrança PIX
        </p>
        {children}
      </div>
    </div>
  );
}
